import { BehaviorSubject } from 'rxjs';
import * as i0 from "@angular/core";
/** How close to the bottom (px) still counts as "scrolled to the bottom". */
const BOTTOM_THRESHOLD_PX = 24;
/** Below this the content is treated as not scrollable, so scrolling cannot reveal anything. */
const SCROLLABLE_THRESHOLD_PX = 8;
/**
 * How much upward scrolling puts the navigation away again. Small on purpose: the whole point of
 * hiding it again is to free the last hours it was covering, so it must let go on the very first
 * real movement, not somewhere deep into a swipe the user has to repeat.
 */
const HIDE_SCROLL_UP_PX = 10;
/** Movement under this is jitter, not a direction. */
const DIRECTION_EPSILON_PX = 1;
/**
 * Shared reveal state for the scheduler navigation.
 *
 * The scheduler hides the navigation to give the weekly grid the whole screen (mobile landscape in
 * the app, the iframe everywhere), which leaves the user with no way out. Two gestures bring it back:
 * scrolling the schedule to its bottom, and tapping the "Scheduler" title.
 *
 * Both flips are driven by the scroll *direction*, never by geometry alone. Showing the chrome
 * shrinks the scroller, hiding it grows it back, and each flip can leave the browser clamping the
 * old scroll position into the new bounds — which fires a scroll event that is not the user
 * scrolling. A rule that re-derives the state from the distance to the bottom would undo itself on
 * that very event and the bar would flicker. What actually marks that event is the resize itself
 * (see lastClientHeight below), not how long ago the flip happened — a time-based freeze would
 * swallow a real swipe that starts while the chrome is still settling, which is exactly when the
 * user goes to scroll it back away.
 */
export class SchedulerNavRevealService {
    constructor() {
        this.revealedSubject = new BehaviorSubject(false);
        this.atBottom = false;
        /** Set by a title tap, cleared as soon as scrolling flips the state again. */
        this.forced = null;
        this.lastScrollTop = 0;
        /**
         * Which scroller lastScrollTop belongs to. Each scheduler page reports its own, and their
         * positions have nothing to do with each other, so carrying one's over to the other reads as a
         * jump in some direction and flips the navigation on a scroll the user never made.
         */
        this.lastScrollElement = null;
        /** Upward distance accumulated since the navigation was revealed. */
        this.scrolledUpBy = 0;
        /**
         * The scroller's own clientHeight as of the last tick. Revealing or hiding the chrome resizes
         * it — that resize, not a fixed time window, is what marks the next tick as reflow noise rather
         * than the user's own scroll.
         */
        this.lastClientHeight = 0;
    }
    /**
     * True when the reveal is (also) backed by having actually reached the bottom of the schedule —
     * as opposed to only a title tap while still scrolled somewhere in the middle of the day. Only
     * this case means hours were just scrolled past to trigger it.
     */
    get isAtBottom() {
        return this.atBottom;
    }
    get revealed$() {
        return this.revealedSubject.asObservable();
    }
    get revealed() {
        return this.revealedSubject.value;
    }
    /** Title tap: show what is hidden, hide what is shown. */
    toggle() {
        this.forced = !this.revealed;
        this.publish();
    }
    /** Recomputes the reveal state from the page scroller. Cheap enough for every scroll tick. */
    syncWithScrollElement(scrollElement) {
        if (!scrollElement) {
            return;
        }
        const scrollTop = scrollElement.scrollTop;
        const clientHeight = scrollElement.clientHeight;
        const previousScrollTop = this.lastScrollTop;
        const previousClientHeight = this.lastClientHeight;
        const sameElement = scrollElement === this.lastScrollElement;
        this.lastScrollElement = scrollElement;
        this.lastScrollTop = scrollTop;
        this.lastClientHeight = clientHeight;
        if (!sameElement) {
            // First tick from this scroller: there is no previous position of its own to compare with.
            this.scrolledUpBy = 0;
            return;
        }
        // The bar showing or hiding just resized this very scroller — the browser may have clamped
        // its position to fit, and that clamp fires a scroll event that is not the user scrolling.
        // Catching it by the resize itself (rather than a fixed window after the flip) means a real
        // swipe that starts the moment the bar appears is never mistaken for that clamp and dropped.
        if (clientHeight !== previousClientHeight) {
            this.scrolledUpBy = 0;
            return;
        }
        if (scrollElement.scrollHeight - clientHeight <= SCROLLABLE_THRESHOLD_PX) {
            return;
        }
        const movedDown = scrollTop > previousScrollTop + DIRECTION_EPSILON_PX;
        const movedUp = scrollTop < previousScrollTop - DIRECTION_EPSILON_PX;
        if (!this.atBottom) {
            const distanceToBottom = scrollElement.scrollHeight - scrollTop - clientHeight;
            if (movedDown && distanceToBottom <= BOTTOM_THRESHOLD_PX) {
                this.setAtBottom(true);
            }
            return;
        }
        if (movedDown) {
            this.scrolledUpBy = 0;
            return;
        }
        if (movedUp) {
            this.scrolledUpBy += previousScrollTop - scrollTop;
            if (this.scrolledUpBy >= HIDE_SCROLL_UP_PX) {
                this.setAtBottom(false);
            }
        }
    }
    /** Leaving the scheduler: the next entry must start hidden again. */
    reset() {
        this.atBottom = false;
        this.forced = null;
        this.lastScrollTop = 0;
        this.lastScrollElement = null;
        this.lastClientHeight = 0;
        this.scrolledUpBy = 0;
        this.publish();
    }
    setAtBottom(atBottom) {
        this.atBottom = atBottom;
        this.scrolledUpBy = 0;
        // Scrolling takes control back from the title tap.
        this.forced = null;
        this.publish();
    }
    publish() {
        const next = this.forced === null ? this.atBottom : this.forced;
        if (next === this.revealedSubject.value) {
            return;
        }
        this.revealedSubject.next(next);
    }
}
SchedulerNavRevealService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function SchedulerNavRevealService_Factory() { return new SchedulerNavRevealService(); }, token: SchedulerNavRevealService, providedIn: "root" });
